import { useEffect, useState } from "react";
import { getDatabase, ref, onValue, update } from "firebase/database";

export const API_BASE_URL = "https://api.deafassistant.com";

/** Per-account display preferences, kept in Firebase at users/<uid>/advancedSettings so they follow the user across devices. */
export interface AdvancedSettings {
  /** Show the "secure QR code" (for streams using "Require authentication") under the regular QR code. */
  secureQr: boolean;
  /** Show Title/Description editing and Hide/Unhide in Transcript History. */
  advancedHistory: boolean;
}

const DEFAULTS: AdvancedSettings = { secureQr: false, advancedHistory: false };

export function useAdvancedSettings(uid: string | undefined) {
  const [settings, setSettings] = useState<AdvancedSettings>(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!uid) return;
    const settingsRef = ref(getDatabase(), `users/${uid}/advancedSettings`);
    return onValue(
      settingsRef,
      (snapshot) => {
        setSettings({ ...DEFAULTS, ...(snapshot.val() || {}) });
        setLoaded(true);
      },
      (error) => {
        console.error("Failed to read advanced settings:", error);
        setLoaded(true);
      }
    );
  }, [uid]);

  const save = (changes: Partial<AdvancedSettings>) => {
    if (!uid) return Promise.resolve();
    setSettings((prev) => ({ ...prev, ...changes }));
    return update(ref(getDatabase(), `users/${uid}/advancedSettings`), changes);
  };

  return { settings, loaded, save };
}

export interface ExpirySettings {
  expiryHours: number;
  expiredMessage: string;
  /** UTC ISO time captions stop being public, or null while the stream is live / has not ended. */
  expiresAt: string | null;
}

export async function fetchExpirySettings(streamName: string, uid: string): Promise<ExpirySettings> {
  const url = new URL(`${API_BASE_URL}/stream/GetExpirySettings`);
  url.search = new URLSearchParams({ streamName: streamName.toLowerCase(), uid }).toString();
  const response = await fetch(url);
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

export async function saveExpirySettings(
  streamName: string,
  uid: string,
  expiryHours: number,
  expiredMessage: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/stream/SetExpirySettings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ streamName: streamName.toLowerCase(), uid, expiryHours, expiredMessage }),
  });
  if (!response.ok) throw new Error(await response.text());
}

export interface AuthCodeInfo {
  code: string;
  /** The secure link: the stream URL plus ?auth=<code>. */
  url: string;
}

export async function fetchAuthCode(streamName: string, uid: string, refresh = false): Promise<AuthCodeInfo> {
  const url = new URL(`${API_BASE_URL}/stream/${refresh ? "RefreshAuthCode" : "GetAuthCode"}`);
  url.search = new URLSearchParams({ streamName: streamName.toLowerCase(), uid }).toString();
  const response = await fetch(url, { method: refresh ? "POST" : "GET" });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export const DEVELOPER_KEY_CONTACT = "hello@conferencecaptioning.com";

export interface DeveloperKeyInfo {
  id: number;
  /** First characters of the key, to tell keys apart. The key itself is never shown again. */
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export class DeveloperKeyLimitError extends Error {
  constructor(public limit: number, message: string) {
    super(message);
  }
}

/** The current login as a Bearer header: developer key management is tied to the signed-in account. */
async function loginHeader(getToken: () => Promise<string>): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getToken()}` };
}

export async function listDeveloperKeys(getToken: () => Promise<string>): Promise<{ limit: number; keys: DeveloperKeyInfo[] }> {
  const response = await fetch(`${API_BASE_URL}/developerkey/List`, { headers: await loginHeader(getToken) });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

/** Returns the new key. It is the only time the key is available. */
export async function createDeveloperKey(getToken: () => Promise<string>): Promise<{ id: number; key: string }> {
  const response = await fetch(`${API_BASE_URL}/developerkey/Create`, { method: "POST", headers: await loginHeader(getToken) });
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    throw new DeveloperKeyLimitError(body.limit ?? 2, body.message ?? "");
  }
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

export async function revokeDeveloperKey(getToken: () => Promise<string>, id: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/developerkey/Revoke?id=${id}`, { method: "POST", headers: await loginHeader(getToken) });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
}
